import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { DayPicker } from "react-day-picker";
import { format, differenceInCalendarDays, isSameDay } from "date-fns";
import { tr, enUS } from "date-fns/locale";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from "lucide-react";
import "react-day-picker/dist/style.css";
import "./date-range-picker.css";

/**
 * Booking.com-style date range picker (full-screen modal).
 * Props:
 *  - value: { from: "YYYY-MM-DD", to: "YYYY-MM-DD" }
 *  - onChange: ({ from, to }) => void
 *  - lang: "tr" | "en"
 *  - variant: "light" | "dark"
 *  - minDate?: Date
 *  - dayPrices?: (date: Date) => string | null   // per-day price label under day number
 *  - testIds?: { checkIn?, checkOut?, cellIn?, cellOut? }
 */
const toISO = (d) => {
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const fromISO = (iso) => {
    if (!iso) return undefined;
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
};

const DP_NAV_LEFT = () => <ChevronLeft className="w-4 h-4" />;
const DP_NAV_RIGHT = () => <ChevronRight className="w-4 h-4" />;

const DateRangePicker = ({
    value,
    onChange,
    lang = "tr",
    variant = "light",
    minDate,
    dayPrices,
    testIds = {},
    labels = {},
}) => {
    const locale = lang === "en" ? enUS : tr;
    const [open, setOpen] = useState(false);
    const [activeSide, setActiveSide] = useState("from");
    const [selected, setSelected] = useState({
        from: fromISO(value?.from),
        to: fromISO(value?.to),
    });
    const [month, setMonth] = useState(fromISO(value?.from) || new Date());
    const [isDesktop, setIsDesktop] = useState(true);

    useEffect(() => {
        const mq = window.matchMedia("(min-width: 768px)");
        const update = () => setIsDesktop(mq.matches);
        update();
        mq.addEventListener?.("change", update);
        return () => mq.removeEventListener?.("change", update);
    }, []);

    useEffect(() => {
        setSelected({ from: fromISO(value?.from), to: fromISO(value?.to) });
    }, [value?.from, value?.to]);

    // Lock body scroll while the calendar modal is open
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const nights = useMemo(() => {
        if (selected?.from && selected?.to) {
            return Math.max(0, differenceInCalendarDays(selected.to, selected.from));
        }
        return 0;
    }, [selected]);

    const fmt = (d) => (d ? format(d, "EEE, d MMM", { locale }) : "");
    const fmtShort = (d) => (d ? format(d, "d MMM yyyy", { locale }) : "");

    const openWith = (side) => {
        setActiveSide(side);
        setMonth(selected?.from || new Date());
        setOpen(true);
    };

    const commit = (range) => {
        setSelected(range || { from: undefined, to: undefined });
        if (range?.from && range?.to && !isSameDay(range.from, range.to)) {
            onChange?.({ from: toISO(range.from), to: toISO(range.to) });
            setTimeout(() => setOpen(false), 150);
        }
    };

    const handleDayClick = (day) => {
        if (!day) return;
        if (activeSide === "from" || !selected?.from) {
            setSelected({ from: day, to: undefined });
            setActiveSide("to");
            return;
        }
        if (selected.from && day > selected.from) {
            commit({ from: selected.from, to: day });
            setActiveSide("from");
            return;
        }
        setSelected({ from: day, to: undefined });
        setActiveSide("to");
    };

    const clearRange = (e) => {
        e.stopPropagation();
        setSelected({ from: undefined, to: undefined });
        setActiveSide("from");
        onChange?.({ from: "", to: "" });
    };

    const L = {
        checkIn: labels.checkIn || (lang === "en" ? "Check-in" : "Giriş Tarihi"),
        checkOut: labels.checkOut || (lang === "en" ? "Check-out" : "Çıkış Tarihi"),
        selectDate: labels.selectDate || (lang === "en" ? "Select date" : "Tarih seç"),
        nights: labels.nights || (lang === "en" ? "nights" : "gece"),
        night: labels.night || (lang === "en" ? "night" : "gece"),
        done: labels.done || (lang === "en" ? "Done" : "Tamam"),
        clear: labels.clear || (lang === "en" ? "Clear" : "Temizle"),
        perNight: lang === "en" ? "price per night" : "gecelik oda fiyatı",
    };

    const nightsLabel = nights === 1 ? L.night : L.nights;

    const isDark = variant === "dark";
    const cellBase = "flex-1 cursor-pointer transition-colors group relative";
    const cellLight = "hover:bg-emerald_deep/5";
    const cellDark = "hover:bg-emerald_deep/20";

    const hasPrices = typeof dayPrices === "function";
    const dpComponents = useMemo(() => {
        const comps = { IconLeft: DP_NAV_LEFT, IconRight: DP_NAV_RIGHT };
        if (hasPrices) {
            comps.DayContent = ({ date }) => {
                const p = dayPrices(date);
                return (
                    <span className="drp-daycell">
                        <span className="drp-daynum">{date.getDate()}</span>
                        {p != null && <span className="drp-dayprice">{p}</span>}
                    </span>
                );
            };
        }
        return comps;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPrices, dayPrices]);

    return (
        <>
            <div
                className={`drp-wrapper ${isDark ? "drp-dark" : "drp-light"} grid grid-cols-2 divide-x ${isDark ? "divide-border_green" : "divide-emerald_deep/10"}`}
                role="button"
                tabIndex={0}
            >
                <div
                    className={`${cellBase} ${isDark ? cellDark : cellLight} p-4`}
                    onClick={(e) => { e.stopPropagation(); openWith("from"); }}
                    data-testid={testIds.cellIn || "date-range-cell-in"}
                >
                    <div className={`text-[11px] uppercase tracking-[0.22em] mb-1 font-bold flex items-center gap-1.5 ${isDark ? "text-bone/70" : "text-emerald-950/70"}`}>
                        <CalendarIcon className="w-3.5 h-3.5" style={{ color: "var(--c-gold)" }} /> {L.checkIn}
                    </div>
                    <div
                        className={`${isDark ? "text-sm" : "text-base"} font-semibold ${isDark ? "text-bone" : "text-emerald-950"} truncate`}
                        data-testid={testIds.checkIn || "date-range-checkin"}
                    >
                        {selected?.from ? fmt(selected.from) : <span className="opacity-60 font-normal">{L.selectDate}</span>}
                    </div>
                </div>
                <div
                    className={`${cellBase} ${isDark ? cellDark : cellLight} p-4`}
                    onClick={(e) => { e.stopPropagation(); openWith("to"); }}
                    data-testid={testIds.cellOut || "date-range-cell-out"}
                >
                    <div className={`text-[11px] uppercase tracking-[0.22em] mb-1 font-bold flex items-center gap-1.5 ${isDark ? "text-bone/70" : "text-emerald-950/70"}`}>
                        <CalendarIcon className="w-3.5 h-3.5" style={{ color: "var(--c-gold)" }} /> {L.checkOut}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                        <div
                            className={`${isDark ? "text-sm" : "text-base"} font-semibold ${isDark ? "text-bone" : "text-emerald-950"} truncate`}
                            data-testid={testIds.checkOut || "date-range-checkout"}
                        >
                            {selected?.to ? fmt(selected.to) : <span className="opacity-60 font-normal">{L.selectDate}</span>}
                        </div>
                        {nights > 0 && !isDark && (
                            <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: "var(--c-gold)", color: "#fff" }}>
                                {nights} {nightsLabel}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {open && createPortal(
                <div className="drp-modal-root" data-testid="date-range-popover">
                    <div className="drp-modal-backdrop" onClick={() => setOpen(false)} />
                    <div className={`drp-modal drp-popover ${hasPrices ? "drp-has-prices" : ""}`}>
                        <div className="drp-header flex items-center justify-between px-5 py-3 border-b">
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald_deep/70">
                                    {activeSide === "from" ? L.checkIn : L.checkOut}
                                </div>
                                <div className="text-sm font-semibold text-emerald_deep mt-0.5">
                                    {selected?.from ? fmtShort(selected.from) : L.selectDate}
                                    {selected?.to && (
                                        <>
                                            <span className="mx-2 text-emerald_deep/40">→</span>
                                            {fmtShort(selected.to)}
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {(selected?.from || selected?.to) && (
                                    <button
                                        type="button"
                                        onClick={clearRange}
                                        className="text-[11px] font-semibold uppercase tracking-wider text-emerald_deep/60 hover:text-emerald_deep flex items-center gap-1 px-2 py-1 rounded"
                                        data-testid="date-range-clear"
                                    >
                                        <X className="w-3 h-3" /> {L.clear}
                                    </button>
                                )}
                                {nights > 0 && (
                                    <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: "var(--c-gold)", color: "#fff" }}>
                                        {nights} {nightsLabel}
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setOpen(false)}
                                    className="w-8 h-8 flex items-center justify-center border border-emerald_deep/20 text-emerald_deep/70 hover:text-emerald_deep hover:border-emerald_deep/50 transition-colors"
                                    aria-label={lang === "en" ? "Close" : "Kapat"}
                                    data-testid="date-range-close"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div className={`drp-body p-2 sm:p-4 ${hasPrices ? "drp-with-prices" : ""}`}>
                            <DayPicker
                                mode="range"
                                selected={selected}
                                onDayClick={handleDayClick}
                                month={month}
                                onMonthChange={setMonth}
                                numberOfMonths={isDesktop ? 2 : 1}
                                pagedNavigation
                                locale={locale}
                                weekStartsOn={1}
                                fromDate={minDate || new Date()}
                                showOutsideDays={false}
                                components={dpComponents}
                            />
                        </div>
                        <div className="drp-footer flex items-center justify-between px-5 py-3 border-t">
                            <span className="text-[10px] uppercase tracking-wider text-emerald_deep/50">
                                {hasPrices ? L.perNight : ""}
                            </span>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="px-5 py-2 text-sm font-bold uppercase tracking-wider text-white hover:brightness-95 transition-all"
                                style={{ background: "var(--c-gold)" }}
                                data-testid="date-range-done"
                            >
                                {L.done}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default DateRangePicker;
